import {createMachine, propsChangedEvent, type Machine, type MachineEventObject, type MachineScope} from '../../runtime/state-machine';
import type {InputProps} from './contract';

type InputContext = {value: string};
type InputEffects = {
    sync(scope: MachineScope<InputContext, InputProps>): void;
    copy(scope: MachineScope<InputContext, InputProps>): void;
    emitInput(scope: MachineScope<InputContext, InputProps>): void;
    emitChange(scope: MachineScope<InputContext, InputProps>): void;
    focus(scope: MachineScope<InputContext, InputProps>): void;
    startCopiedTimer(scope: MachineScope<InputContext, InputProps>): void;
};
const inputHooks = [] as const;
const eventValue = (event: MachineEventObject): string => typeof event.value === 'string' ? event.value : '';
const setValue = (scope: MachineScope<InputContext, InputProps>) => ({context: {value: eventValue(scope.event)}});
const clearValue = () => ({context: {value: ''}});
const reconcileProps = (scope: MachineScope<InputContext, InputProps>) => {
    const event = propsChangedEvent<InputProps>(scope.event);
    if (!event) return;
    const valueChanged = event.changes.some(({key}) => key === 'value');
    return {props: event.props, context: valueChanged ? {value: event.props.value} : scope.context};
};

const createInputMachine = ({props, effects}: {props: InputProps; effects: InputEffects}): Machine<InputContext, InputProps> => createMachine<InputContext, InputProps>({
    initial: 'idle',
    on: {
        VALUE_CHANGED: {reduce: 'setValue', effects: 'sync'},
        CLEAR: {target: 'idle', reduce: 'clearValue', effects: ['sync', 'emitInput', 'emitChange', 'focus']},
        COPY: {target: 'copying', effects: 'copy'},
        PROPS_CHANGED: {reduce: 'reconcileProps', effects: 'sync'},
    },
    states: {
        idle: {},
        copying: {on: {
            COPY_DONE: {target: 'copied', effects: ['sync', 'startCopiedTimer']},
            COPY_ERROR: {target: 'idle', effects: 'sync'},
        }},
        copied: {tags: ['copied'], on: {
            COPY: {target: 'copying', effects: 'copy'},
            COPIED_TIMEOUT: {target: 'idle', effects: 'sync'},
        }},
    },
}, {
    context: {value: props.value},
    props,
    reducers: {setValue, clearValue, reconcileProps},
    effects,
});

export {createInputMachine, inputHooks};
export type {InputContext, InputEffects};
